import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { services } from '../mockData';

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122"
            alt="Profab Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-gray-900/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Comprehensive metal fabrication solutions for all your needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {services.map((service) => {
              const IconComponent = Icons[service.icon] || Icons.Wrench;
              return (
                <div
                  key={service.id}
                  className="group bg-white border border-gray-200 rounded-lg overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <IconComponent className="text-white" size={32} />
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-profab-blue font-semibold hover:text-profab-blue-dark transition-colors group-hover:translate-x-1 transition-transform"
                    >
                      Request a Quote
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A streamlined approach to ensure quality results every time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-profab-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Consultation
              </h3>
              <p className="text-gray-600">
                We discuss your project requirements, timeline, and budget to understand your vision.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-profab-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Design & Planning
              </h3>
              <p className="text-gray-600">
                Our engineers create detailed plans and specifications for your approval.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-profab-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Fabrication
              </h3>
              <p className="text-gray-600">
                Skilled craftsmen bring your project to life with precision and attention to detail.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-profab-blue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Installation
              </h3>
              <p className="text-gray-600">
                Professional installation ensures everything is completed to the highest standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Advanced Capabilities
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our state-of-the-art facility is equipped with the latest technology and machinery 
                to handle projects of any size and complexity.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    CNC cutting and forming for precision results
                  </span>
                </li>
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    MIG, TIG, and stick welding capabilities
                  </span>
                </li>
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    Powder coating and finishing services
                  </span>
                </li>
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    Custom design and engineering support
                  </span>
                </li>
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    Project management from concept to completion
                  </span>
                </li>
                <li className="flex items-start">
                  <Icons.CheckCircle className="text-profab-blue flex-shrink-0 mt-1" size={24} />
                  <span className="ml-3 text-gray-700">
                    Quality assurance and testing protocols
                  </span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1529479627062-5f1f0b88912a"
                alt="Profab capabilities"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted expertise across multiple sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { name: 'Commercial Construction', icon: 'Building2' },
              { name: 'Industrial Manufacturing', icon: 'Factory' },
              { name: 'Infrastructure', icon: 'Bridge' },
              { name: 'Energy & Utilities', icon: 'Zap' },
              { name: 'Transportation', icon: 'Truck' },
              { name: 'Architecture', icon: 'Ruler' },
              { name: 'Agriculture', icon: 'Sprout' },
              { name: 'Mining', icon: 'Mountain' }
            ].map((industry, index) => {
              const IconComponent = Icons[industry.icon] || Icons.Building2;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg p-6 text-center transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer"
                >
                  <div className="w-16 h-16 bg-profab-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="text-profab-blue" size={28} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {industry.name}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-profab-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Our team is here to provide expert guidance and competitive quotes
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-4 bg-white text-profab-blue rounded-md font-semibold text-lg transition-all hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Get a Free Quote
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
